/**
 * Adaptateur de la configuration tenant.
 *
 * `tenant_config` porte trois colonnes JSON — `settings`, `features`,
 * `branding`. Les deux premières sont lues ici ; `features` attend son
 * domaine. Les écritures passent par des fonctions de fusion plutôt que par
 * un `update` : remplacer la colonne entière effacerait ce qu'un réglage
 * voisin y aura posé.
 */
import {
  brandingFrom,
  cadenceFrom,
  DomainError,
  marginTargetFrom,
  overtimeRatesFrom,
  safetySettingsFrom,
  subcontractingSettingsFrom,
} from "@dker/core";
import type {
  ExecutionContext,
  MarginTarget,
  OvertimeRates,
  SafetySettings,
  SubcontractingSettings,
  ReminderCadence,
  TenantBranding,
  TenantConfig,
  TenantConfigRepository,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseTenantConfigRepository(
  clientFor: DbClientFactory,
): TenantConfigRepository {
  async function read(ctx: ExecutionContext): Promise<TenantConfig> {
    const { data, error } = await clientFor(ctx)
      .from("tenant_config")
      // Pas de `.eq("tenant_id", ...)` : la policy limite déjà la lecture au
      // tenant de la session.
      .select("settings, branding")
      .maybeSingle();
    if (error) fail(error);

    // `maybeSingle` plutôt que `single` : un tenant créé avant le trigger qui
    // pose la ligne n'en a pas, et ce n'est pas une erreur — ce sont les
    // valeurs par défaut.
    return {
      reminders: cadenceFrom(data?.settings?.reminders),
      branding: brandingFrom(data?.branding),
      marginTarget: marginTargetFrom(data?.settings?.margin),
      overtime: overtimeRatesFrom(data?.settings?.overtime),
      safety: safetySettingsFrom(data?.settings?.safety),
      subcontracting: subcontractingSettingsFrom(data?.settings?.subcontracting),
    };
  }

  /**
   * Chaque écriture est suivie d'une relecture complète.
   *
   * Les deux fonctions d'écriture ne renvoient que la colonne qu'elles
   * touchent, alors que l'appelant attend une configuration entière. Recoller
   * les morceaux à la main demanderait de savoir, à chaque écriture, ce que
   * les autres colonnes contenaient — donc de les relire quand même, mais une
   * par une. Une relecture, un seul chemin, et la valeur rendue est celle qui
   * est réellement en base.
   */
  return {
    read,

    async saveReminders(
      ctx: ExecutionContext,
      cadence: ReminderCadence,
    ): Promise<TenantConfig> {
      const { error } = await clientFor(ctx).rpc("set_reminder_cadence", {
        p_cadence: cadence,
      });
      if (error) fail(error);
      return read(ctx);
    },

    async saveBranding(
      ctx: ExecutionContext,
      branding: TenantBranding,
    ): Promise<TenantConfig> {
      // Deux paramètres plutôt qu'un JSON : la fonction écrit en snake_case
      // pour rester lisible par `branding_for_host`, et lui passer un objet
      // camelCase inviterait à le recopier tel quel un jour.
      const { error } = await clientFor(ctx).rpc("set_tenant_branding", {
        p_logo_url: branding.logoUrl,
        p_primary_color: branding.primaryColor,
      });
      if (error) fail(error);
      return read(ctx);
    },

    async saveMarginTarget(
      ctx: ExecutionContext,
      targetPercent: MarginTarget,
    ): Promise<TenantConfig> {
      // `null` part tel quel jusqu'à la fonction, qui l'inscrit en JSON null :
      // c'est ainsi que « je retire mon objectif » se distingue de « je n'ai
      // jamais rien posé ». Les deux se relisent pareil, mais l'écriture doit
      // pouvoir effacer.
      const { error } = await clientFor(ctx).rpc("set_margin_target", {
        p_target_percent: targetPercent,
      });
      if (error) fail(error);
      return read(ctx);
    },

    async saveOvertimeRates(ctx: ExecutionContext, rates: OvertimeRates): Promise<TenantConfig> {
      const { error } = await clientFor(ctx).rpc("set_overtime_rates", {
        p_complementary: rates.complementaryRate,
        p_first: rates.firstRate,
        p_second: rates.secondRate,
      });
      if (error) fail(error);
      return read(ctx);
    },

    async saveSafetySettings(ctx: ExecutionContext, settings: SafetySettings): Promise<TenantConfig> {
      const { error } = await clientFor(ctx).rpc("set_safety_talk_frequency", {
        p_days: settings.talkFrequencyDays,
      });
      if (error) fail(error);
      return read(ctx);
    },

    async saveSubcontractingSettings(ctx: ExecutionContext, settings: SubcontractingSettings): Promise<TenantConfig> {
      const { error } = await clientFor(ctx).rpc("set_subcontracting_settings", {
        p_apply_vigilance_threshold: settings.applyVigilanceThreshold,
        p_vigilance_basis: settings.vigilanceBasis,
      });
      if (error) fail(error);
      return read(ctx);
    },
  };
}
